import { MICE_EVENT_RULES, openEventRules } from '@voll-intelligence/domain';
import type { ChatUi } from '@voll-intelligence/genui-contract';
import type { GenUiRenderProps } from '../../../shared/ui/registry.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';

type Block = Extract<ChatUi, { kind: 'event_premises' }>;

/** Data ISO (`YYYY-MM-DD`) no locale do card, sem deslocar pelo fuso. */
function formatDay(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${iso}T00:00:00Z`));
}

/** Estado da ficha: com `pending` conta as regras de viagem em aberto (as pendentes e as que o
 *  rascunho ainda não tem); sem ele (agente antigo) cai na regra antiga, pelo `missing`. Nunca diz
 *  "pronto" com chave faltando: a confirmação recusaria. */
function premisesStatus(ui: Block, t: ReturnType<typeof useGenUiI18n>['t']): string {
  if (ui.confirmed) return t('eventPremises.published');
  if (ui.pending) {
    const open = openEventRules({ rulesPending: ui.pending, missingPremises: ui.missing });
    if (open.length > 0) {
      const total = MICE_EVENT_RULES.length;
      return t('eventPremises.draft', { done: total - open.length, total });
    }
    if (ui.missing.length === 0) return t('eventPremises.readyToPublish');
  }
  if (ui.missing.length === 0) return t('eventPremises.ready');
  const count = ui.missing.length;
  return t(count === 1 ? 'eventPremises.missing_one' : 'eventPremises.missing_other', { count });
}

/** Linha compacta: nome do evento, período e o estado da ficha. */
export function EventPremisesCard({ ui }: Readonly<GenUiRenderProps<Block>>) {
  const { t, locale } = useGenUiI18n();
  const status = premisesStatus(ui, t);
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-border px-3 py-2">
      <span className="text-sm/[19px] font-medium text-foreground">{ui.name}</span>
      <span className="text-caption text-muted-foreground">
        {/* Evento de um dia: uma data só, nunca "18 de dez. – 18 de dez.". */}
        {ui.eventEnd === ui.eventStart
          ? formatDay(ui.eventStart, locale)
          : `${formatDay(ui.eventStart, locale)} – ${formatDay(ui.eventEnd, locale)}`}
      </span>
      <span className="text-caption text-muted-foreground">{status}</span>
    </div>
  );
}
